import { useEffect, useMemo, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton,
  IonList, IonItem, IonLabel, IonNote, IonInput, IonText, IonSearchbar, IonButton, IonIcon,
  IonModal, IonToast, IonSegment, IonSegmentButton, IonSpinner, useIonRouter,
} from '@ionic/react';
import { downloadOutline, documentTextOutline, createOutline, barChartOutline, sparklesOutline, openOutline } from 'ionicons/icons';
import { convertQty, canonicalDdtSupplierIdentities, diagnoseRecipeCost, dedupeStockInByDdt, computeDishEconomics, detectCostAnomalies, type Recipe, type FoodCostSignal, type CostAnomaly } from '@trackeat/shared';
import {
  useMasters,
  useUserTenants,
  useEvents,
  useEnqueueEvent,
  useEnqueueMaster,
  useFoodCostAccessGate,
  useDefaultVatRate,
  useStockProjection,
} from '../../lib/data/queries.js';
import { RecipeCostFixModal } from './RecipeCostFixModal.js';
import { FoodCostAnomalyCard } from './FoodCostAnomalyCard.js';
import { purchaseDate, purchaseIssues, purchaseGroup as purchaseGroupForLine, purchasePeriods, selectPurchasePeriod, summarizePurchases, purchasePriceChanges, type PurchaseGroup } from '../../lib/foodCostPurchases.js';
import './FoodCost.css';
import { getSupabase } from '../../lib/supabase.js';
import { openExternalUrl } from '../../lib/openExternalUrl.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { toUserMessage } from '../../lib/errors.js';
import { FoodCostReport, type FoodCostReportData } from './FoodCostReport.js';
import { RestaurantDecisionCockpit, type RestaurantDecisionCockpitRow } from './RestaurantDecisionCockpit.js';
import {
  csvFoodCostPerRecipe, csvListinoCosti, csvStoricoPrezzi, csvReportAcquisti,
  csvAbcIngredienti, csvValorizzazione, csvSchedeTecniche, toCsv,
  type ExpFood, type ExpStockIn,
} from '../../lib/foodCostExports.js';
import {
  projectFoodCostPairs,
  type CostAdjustmentEventRow,
} from '../../lib/foodCostProjection.js';
import {
  selectActivePhysicalPurchaseReceipts,
  type PhysicalPurchaseEvent,
  type PhysicalPurchasePayload,
} from '../../lib/physicalPurchaseReceipts.js';
import { foodCostDocumentOriginByFood } from '../../lib/foodCostDocumentOrigin.js';
import { SalesImportPanel } from './SalesImportPanel.js';

// T-287/T-301 (Food Cost): pagina del modulo Food Cost.
// Costo per alimento = coppia `foods.last_unit_cost` +
// `foods.last_unit_cost_uom`, denormalizzata e ordinata server-side per data
// commerciale. Un costo senza la sua UdM non viene mai applicato per ipotesi.
// `diagnoseRecipeCost` dà costo PARZIALE + semaforo: verde=tutti valorizzati,
// arancio=parziale, rosso=nessuno (mai uno 0 falso).

type FoodRow = {
  id: string;
  name?: string;
  default_uom?: string | null;
  last_unit_cost?: number | null;
  last_unit_cost_uom?: string | null;
  last_unit_cost_currency?: string | null;
  last_unit_cost_event_id?: string | null;
  last_unit_cost_document_date?: string | null;
  last_unit_cost_source_kind?: 'purchase' | 'manual_adjustment' | null;
  prev_unit_cost?: number | null;
  last_unit_cost_at?: string | null;
  cost_uom_reviewed_at?: string | null;
  cost_uom_reviewed_uom?: string | null;
  current_supplier_id?: string | null;
  category?: string | null;
  version: number;
};
type StockEvt = PhysicalPurchaseEvent & {
  payload: PhysicalPurchasePayload & {
    food_id?: string;
    qty?: number;
    uom?: string | null;
    unit_cost?: number | null;
    line_total?: number | null;
    supplier_id?: string | null;
    supplier_identity?: string | null;
    document_number?: string | null;
    document_id?: string | null;
    document_date?: string | null;
  };
};
type SupplierRow = {
  id: string;
  name?: string | null;
  piva?: string | null;
  aliases?: string[] | null;
};
const REASON_LABEL: Record<string, string> = {
  no_cost_inputs: 'Nessun ingrediente con quantità',
  ingredient_qty_missing: 'Manca una quantità ingrediente',
  ingredient_cost_unknown: 'Costo di un ingrediente non ancora noto',
  ingredient_uom_mismatch: 'Unità ingrediente ≠ unità del costo',
  sub_recipe_not_found: 'Semilavorato non trovato',
  sub_recipe_qty_unknown: 'Quantità semilavorato mancante',
  sub_recipe_uom_mismatch: 'Unità semilavorato ≠ resa',
  sub_recipe_cycle: 'Ricetta ciclica',
  sub_recipe_unavailable: 'Costo di un semilavorato non disponibile',
};
const DOT: Record<string, string> = { green: '#15803d', orange: '#d97706', red: '#dc2626' };
const eur = (n: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n);

// Esportazioni disponibili ORA (Fase 1: dati già nell'app) + quelle "in arrivo"
// (Fase 2/3: servono consumo magazzino / dati di vendita POS). Vedi
// docs/FOOD_COST_ANALYTICS_STRATEGY.md.
const EXPORTS_ACTIVE: { key: string; title: string; desc: string }[] = [
  { key: 'food-cost', title: 'Food cost per piatto', desc: 'Costo, food cost %, margine, stato' },
  { key: 'schede', title: 'Schede tecniche di costo', desc: 'Distinta base con costo e incidenza %' },
  { key: 'listino', title: 'Listino costi materia prima', desc: 'Costo attuale, unità, valuta e aggiornamento' },
  { key: 'storico', title: 'Storico prezzi & variazioni', desc: 'Prezzo nel tempo per alimento' },
  { key: 'alert', title: 'Alert variazione prezzo', desc: 'Alimenti con variazione ≥ 10%' },
  { key: 'acquisti', title: 'Report acquisti', desc: 'Spesa per fornitore × mese' },
  { key: 'abc', title: 'Analisi ABC ingredienti', desc: 'Il 20% che fa l’80% della spesa' },
  { key: 'valorizzazione', title: 'Valorizzazione magazzino', desc: 'Valore trasportato dai movimenti di lotto' },
];

const normalizedUom = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase().replace(/\.+$/, '');

export function FoodCostPage(): JSX.Element {
  const access = useFoodCostAccessGate();
  if (access.checking) {
    return (
      <IonPage>
        <IonHeader><IonToolbar><IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons><IonTitle>Food Cost ristorante</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding"><div style={{ textAlign: 'center', marginTop: 40 }}><IonSpinner /><p style={{ color: '#64748b' }}>Verifica accesso al modulo…</p></div></IonContent>
      </IonPage>
    );
  }
  if (!access.enabled) {
    return (
      <IonPage>
        <IonHeader><IonToolbar><IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons><IonTitle>Food Cost ristorante</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding">
          <IonText color="medium"><p>{access.error ? 'Non è stato possibile verificare l\'accesso. ' : ''}Il modulo <b>Food Cost ristorante</b> non è attivo per questo locale. Il consulente può attivarlo da Impostazioni.</p></IonText>
        </IonContent>
      </IonPage>
    );
  }
  return <EnabledFoodCostPage />;
}

function EnabledFoodCostPage(): JSX.Element {
  // T-450 F1: aliquota IVA di default del locale (override per ricetta via
  // recipe.vat_rate). Serve a scorporare l'IVA dal prezzo di vendita (lordo).
  const defaultVat = useDefaultVatRate();
  const { profile } = useAuth();
  const router = useIonRouter();
  // T-342: apre l'editor ricetta completo in RecipesPage (deep-link).
  const openRecipeEditor = (id: string) => router.push(`/haccp/recipes?edit=${id}`, 'forward');
  const recipesQuery = useMasters<Recipe>('recipes');
  const foodsQuery = useMasters<FoodRow>('foods');
  const stockInQuery = useEvents('stock_in', null);
  const stockOutQuery = useEvents('stock_out', null);
  const adjustmentsQuery = useEvents('cost_adjustment', null);
  const documentsQuery = useMasters<{ id: string; date: string | null }>('documents');
  const { data: recipesRaw = [] } = recipesQuery;
  const { data: foods = [] } = foodsQuery;
  const { data: stockIn = [] } = stockInQuery;
  const { data: stockOut = [] } = stockOutQuery;
  const { data: costAdjustments = [] } = adjustmentsQuery;
  const { data: documents = [] } = documentsQuery;
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      (foods as FoodRow[])
        .filter((food) => !!food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!.trim()]),
    ),
    [foods],
  );
  const stockProjection = useStockProjection({ canonicalUomByFood });
  const { data: suppliers = [] } = useMasters<SupplierRow>('suppliers');
  const enqueueEvent = useEnqueueEvent();
  const enqueueMaster = useEnqueueMaster();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [fixRecipeId, setFixRecipeId] = useState<string | null>(null);
  // T-462: correzione del costo di UN alimento (tap dalla card "Controllo prezzi").
  const [fixFood, setFixFood] = useState<FoodRow | null>(null);
  const [fixAnomaly, setFixAnomaly] = useState<CostAnomaly | null>(null);
  const [costDraft, setCostDraft] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useState<'report' | 'purchases' | 'list' | 'checks' | 'sales'>('report');
  const [periodKey, setPeriodKey] = useState('month');
  const [purchaseFilter, setPurchaseFilter] = useState<PurchaseGroup | 'all'>('all');
  const [target, setTarget] = useState(30);
  const periods = useMemo(() => purchasePeriods(), []);
  const period = periods[periodKey] ?? periods.month;
  const periodName = ({ month: 'Mese in corso', previous: 'Mese precedente', quarter: 'Ultimi 3 mesi', all: 'Tutto lo storico datato' } as Record<string, string>)[periodKey];
  const periodLabel = period.from && period.to ? `${periodName} · ${period.from.split('-').reverse().join('/')} – ${period.to.split('-').reverse().join('/')}` : periodName;
  const { data: tenants = [] } = useUserTenants();
  const tenantLabel = tenants.find((tenant) => tenant.id === profile?.active_tenant_id)?.name ?? 'Azienda selezionata';

  // T-443: un DDT reimportato N volte NON deve contare N volte nel food cost
  // («un DDT = un carico, una volta sola», owner). `dedupeStockInByDdt` tiene
  // solo l'import PIÙ RECENTE per identità commerciale fornitore + numero +
  // anno (i re-import automatici hanno un `document_id` nuovo ogni volta); i
  // carichi manuali restano intatti. Tutti i consumatori del food cost
  // (costo/kg, spesa, valorizzazione, export) leggono da qui.
  const purchaseStockIn = useMemo(
    () => selectActivePhysicalPurchaseReceipts(
      stockIn as StockEvt[],
      stockOut as PhysicalPurchaseEvent[],
    ),
    [stockIn, stockOut],
  );
  const canonicalSupplierIdentityById = useMemo(
    () => canonicalDdtSupplierIdentities(suppliers as SupplierRow[]),
    [suppliers],
  );
  const dedupedStockIn = useMemo(
    () => dedupeStockInByDdt(purchaseStockIn.map((event) => {
      const supplierId = event.payload?.supplier_id ?? null;
      const canonicalIdentity = supplierId
        ? canonicalSupplierIdentityById.get(supplierId) ?? null
        : null;
      if (!canonicalIdentity) return event;
      return {
        ...event,
        payload: {
          ...event.payload,
          supplier_identity: canonicalIdentity,
        },
      };
    })) as StockEvt[],
    [canonicalSupplierIdentityById, purchaseStockIn],
  );
  const sourceDocumentByFood = useMemo(
    () => foodCostDocumentOriginByFood(foods as FoodRow[], purchaseStockIn as StockEvt[]),
    [foods, purchaseStockIn],
  );

  // Costo e UdM sono una coppia denormalizzata server-side ordinata per data
  // commerciale. Non preferiamo l'ultimo evento importato: una fattura vecchia
  // caricata oggi non deve sostituire il prezzo di un acquisto più recente.
  const foodCostPairs = useMemo(
    () => projectFoodCostPairs(
      foods as FoodRow[],
      costAdjustments as CostAdjustmentEventRow[],
    ),
    [costAdjustments, foods],
  );
  const foodUnitCost = useMemo(
    () => (id: string): number | null => { const pair = foodCostPairs.get(id); return pair?.currency === 'EUR' ? pair.cost : null; },
    [foodCostPairs],
  );
  const foodCostUom = useMemo(
    () => (id: string): string | null => foodCostPairs.get(id)?.uom ?? null,
    [foodCostPairs],
  );

  // Il costo scelto e la sua unità devono provenire dalla stessa fonte. Usare
  // il default dell'alimento accanto a un prezzo dell'ultimo carico espresso in
  // un'altra UdM renderebbe compatibile un numero dimensionalmente sbagliato.
  const foodUom = foodCostUom;

  const recipesById = useMemo(() => {
    const m = new Map<string, Recipe>();
    for (const r of recipesRaw as Recipe[]) m.set(r.id, r);
    return m;
  }, [recipesRaw]);

  // T-462/T-476 "Controllo prezzi": manteniamo anche la data di ogni UdM.
  // Una revisione umana può così chiudere un errore storico senza cancellare
  // l'evento; qualunque carico successivo viene controllato di nuovo.
  const priceAnomalies = useMemo(() => {
    const loadUoms = new Map<string, Array<{ uom: string; observedAt: string | null }>>();
    for (const e of dedupedStockIn) {
      const fid = e.payload?.food_id; const u = e.payload?.uom;
      if (!fid || !u) continue;
      if (!loadUoms.has(fid)) loadUoms.set(fid, []);
      loadUoms.get(fid)!.push({ uom: String(u), observedAt: e.server_ts ?? e.client_ts ?? null });
    }
    const signals: FoodCostSignal[] = (foods as FoodRow[]).map((f) => ({
      id: f.id, name: f.name ?? f.id.slice(0, 8), category: f.category ?? null,
      defaultUom: f.default_uom ?? null,
      costUom: foodCostPairs.get(f.id)?.uom ?? null,
      lastUnitCost: foodCostPairs.get(f.id)?.cost ?? null,
      // Legacy previous price has no unit/currency: never compare it blindly.
      prevUnitCost: null,
      loadUomObservations: loadUoms.get(f.id) ?? [],
      uomReviewedAt: f.cost_uom_reviewed_at ?? null,
      uomReviewedAs: f.cost_uom_reviewed_uom ?? null,
    }));
    return detectCostAnomalies(signals);
  }, [dedupedStockIn, foodCostPairs, foods]);

  // T-473: cockpit ristorante fail-closed. Una ricetta entra nei calcoli
  // decisionali solo se costo/porzione e prezzo sono completi e nessun alimento
  // (anche dentro un semilavorato) ha un'anomalia di costo ad alta severità.
  const restaurantDecisionRows = useMemo<RestaurantDecisionCockpitRow[]>(() => {
    const ctx = { recipesById, foodUnitCost, foodUom };
    const highAnomalyByFood = new Map(
      priceAnomalies.filter((item) => item.severity === 'alta' || item.kind === 'costo_unita_diversa').map((item) => [item.foodId, item]),
    );
    const collectFoodIds = (recipe: Recipe, seen: Set<string>): Set<string> => {
      if (seen.has(recipe.id)) return new Set();
      const nextSeen = new Set(seen).add(recipe.id);
      const ids = new Set((recipe.ingredients ?? []).map((ingredient) => ingredient.food_id));
      for (const subId of recipe.parent_recipe_ids ?? []) {
        const sub = recipesById.get(subId);
        if (sub) for (const id of collectFoodIds(sub, nextSeen)) ids.add(id);
      }
      return ids;
    };
    return (recipesRaw as Recipe[])
      // Il cockpit decide i prezzi del MENU: i semilavorati restano nel grafo
      // del costo, ma non sono articoli venduti e non devono risultare
      // "incompleti" solo perché non hanno un prezzo di vendita.
      .filter((recipe) => !recipe.archived_at && recipe.variant_of_recipe_id == null && (recipe.is_food_cost_only || (recipe.selling_price_cents ?? 0) > 0))
      .map((recipe): RestaurantDecisionCockpitRow => {
        const diagnosis = diagnoseRecipeCost(recipe, ctx);
        if (diagnosis.severity !== 'green' || diagnosis.costPerPortion == null) {
          const reason = diagnosis.missing[0]?.reason;
          return {
            id: recipe.id, name: recipe.name, status: 'incomplete',
            costPerPortion: diagnosis.costPerPortion,
            sellingPriceCentsGross: recipe.selling_price_cents ?? null,
            vatRatePct: recipe.vat_rate ?? defaultVat,
            detail: reason ? (REASON_LABEL[reason] ?? reason) : 'Imposta ingredienti, quantità e numero di porzioni.',
          };
        }
        const anomaly = [...collectFoodIds(recipe, new Set())]
          .map((foodId) => highAnomalyByFood.get(foodId))
          .find((item) => item != null);
        if (anomaly) {
          return {
            id: recipe.id, name: recipe.name, status: 'anomaly',
            costPerPortion: diagnosis.costPerPortion,
            sellingPriceCentsGross: recipe.selling_price_cents ?? null,
            vatRatePct: recipe.vat_rate ?? defaultVat,
            blockerFoodId: anomaly.foodId,
            detail: `${anomaly.name}: ${anomaly.detail}`,
          };
        }
        if (recipe.selling_price_cents == null || recipe.selling_price_cents <= 0 || !Number.isFinite(recipe.selling_price_cents) || !Number.isFinite(recipe.vat_rate ?? defaultVat) || (recipe.vat_rate ?? defaultVat) < 0) {
          return {
            id: recipe.id, name: recipe.name, status: 'incomplete',
            costPerPortion: diagnosis.costPerPortion,
            sellingPriceCentsGross: recipe.selling_price_cents ?? null,
            vatRatePct: recipe.vat_rate ?? defaultVat,
            detail: 'Verifica prezzo di vendita lordo e aliquota IVA.',
          };
        }
        return {
          id: recipe.id, name: recipe.name, status: 'ready',
          costPerPortion: diagnosis.costPerPortion,
          sellingPriceCentsGross: recipe.selling_price_cents,
          vatRatePct: recipe.vat_rate ?? defaultVat,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [defaultVat, foodUnitCost, foodUom, priceAnomalies, recipesById, recipesRaw]);

  function openFoodCostFix(foodId: string): void {
    const f = (foods as FoodRow[]).find((x) => x.id === foodId);
    if (!f) return;
    setFixFood(f);
    setFixAnomaly(priceAnomalies.find((item) => item.foodId === foodId) ?? null);
    const pair = foodCostPairs.get(foodId);
    const factor = f.default_uom && pair?.uom ? convertQty(1, f.default_uom, pair.uom) : null;
    const currentCost = pair?.currency === 'EUR' && pair.cost != null && factor != null ? pair.cost * factor : null;
    setCostDraft(currentCost != null ? String(Number(currentCost.toFixed(6))) : '');
  }

  function openFoodSourceDocument(foodId: string): void {
    const documentId = sourceDocumentByFood.get(foodId);
    if (!documentId) {
      setToast('Nessun DDT di origine collegato a questo costo. La correzione manuale resta disponibile separatamente.');
      return;
    }
    router.push(`/documents/public/${documentId}`, 'forward');
  }

  async function confirmFoodUom(): Promise<void> {
    if (!fixFood?.default_uom || fixAnomaly?.kind !== 'unita_miste') return;
    const latest = normalizedUom(fixAnomaly.latestLoadUom);
    if (latest !== normalizedUom(fixFood.default_uom)) {
      setToast(`L'ultimo carico è in ${fixAnomaly.latestLoadUom ?? 'unità ignota'}: non posso confermare ${fixFood.default_uom}.`);
      return;
    }
    try {
      await enqueueMaster.mutateAsync({
        op: 'update', table: 'foods', id: fixFood.id,
        patch: {
          cost_uom_reviewed_at: new Date().toISOString(),
          cost_uom_reviewed_uom: fixFood.default_uom,
        },
        base_version: fixFood.version,
      });
      setToast(`Unità confermata: ${fixFood.name ?? 'alimento'} in ${fixFood.default_uom}`);
      setFixFood(null);
      setFixAnomaly(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function saveFoodCostFix(): Promise<void> {
    if (!fixFood) return;
    const raw = costDraft.trim().replace(',', '.');
    const v = raw === '' ? null : Number(raw);
    if (v !== null && (!Number.isFinite(v) || v < 0)) { setToast('Inserisci un costo valido (≥ 0)'); return; }
    const costUom = fixFood.default_uom?.trim() || null;
    if (v !== null && !costUom) {
      setToast('Imposta prima l’unità di misura dell’alimento');
      return;
    }
    try {
      const now = new Date().toISOString();
      await enqueueEvent.mutateAsync({
        kind: 'cost_adjustment',
        payload: {
          food_id: fixFood.id,
          unit_cost: v,
          cost_uom: v == null ? null : costUom,
          currency: v == null ? null : 'EUR',
          effective_at: now,
          reason: 'manual_food_cost_fix',
        },
        clientTs: now,
      });
      setToast(`Costo aggiornato: ${fixFood.name ?? 'alimento'}`);
      setFixFood(null);
      setFixAnomaly(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  const rows = useMemo(() => {
    const ctx = { recipesById, foodUnitCost, foodUom };
    const term = search.trim().toLowerCase();
    return (recipesRaw as Recipe[])
      // T-441: le varianti dieta (ricette figlie) non hanno food cost proprio → nascoste.
      .filter((r) => !r.archived_at && r.variant_of_recipe_id == null)
      .filter((r) => term === '' || r.name.toLowerCase().includes(term))
      .map((r) => ({ recipe: r, diag: diagnoseRecipeCost(r, ctx) }))
      .sort((a, b) => a.recipe.name.localeCompare(b.recipe.name));
  }, [recipesRaw, recipesById, foodUnitCost, foodUom, search]);

  // Purchases and current recipe economics are separate, explicit scopes.
  const purchaseLines = useMemo<ExpStockIn[]>(() => {
    const categories = new Map((foods as FoodRow[]).map((food) => [food.id, food.category]));
    const documentDates = new Map(documents.map((document) => [document.id, document.date]));
    return dedupedStockIn.filter((event) => event.payload?.food_id).map((event) => ({
      food_id: event.payload.food_id!, qty: event.payload.qty ?? null,
      unit_cost: event.payload.unit_cost ?? null, line_total: event.payload.line_total ?? null,
      supplier_id: event.payload.supplier_id ?? null, observed_at: event.server_ts ?? event.client_ts ?? '',
      document_date: event.payload.document_date ?? documentDates.get(event.payload.document_id ?? '') ?? null, document_id: event.payload.document_id ?? null,
      uom: event.payload.uom ?? null,
      currency: typeof event.payload.currency === 'string' ? event.payload.currency : 'EUR',
      category: categories.get(event.payload.food_id!) ?? null,
    }));
  }, [dedupedStockIn, foods, documents]);
  const periodLines = useMemo(() => selectPurchasePeriod(purchaseLines, period), [purchaseLines, period]);
  const filteredPurchaseLines = useMemo(() => periodLines.filter((line) => purchaseFilter === 'all'
    || purchaseGroupForLine(line.category) === purchaseFilter), [periodLines, purchaseFilter]);
  const purchaseSummary = useMemo(() => summarizePurchases(filteredPurchaseLines), [filteredPurchaseLines]);
  const purchaseIssueLines = useMemo(() => purchaseIssues(purchaseLines, period, purchaseFilter), [purchaseLines, period, purchaseFilter]);
  const allGroups = useMemo(() => summarizePurchases(periodLines).groups, [periodLines]);
  const priceChanges = useMemo(() => purchasePriceChanges(filteredPurchaseLines,
    (id) => canonicalUomByFood[id] ?? null), [filteredPurchaseLines, canonicalUomByFood]);
  const reportData: FoodCostReportData = useMemo(() => {
    const ready = restaurantDecisionRows.filter((row) => row.status === 'ready');
    const complete = ready.map((row) => ({ row, econ: computeDishEconomics(row) }));
    const percentages = complete.flatMap(({ econ }) => econ.foodCostPct == null ? [] : [econ.foodCostPct]);
    const fById = new Map((foods as FoodRow[]).map((food) => [food.id, food.name ?? food.id]));
    const sById = new Map((suppliers as SupplierRow[]).map((supplier) => [supplier.id, supplier.name ?? 'Senza fornitore']));
    const byValue = (map: Map<string, number>) => [...map].sort((a, b) => b[1] - a[1]);
    return {
      kpi: {
        avgFoodCostPct: percentages.length ? percentages.reduce((sum, value) => sum + value, 0) / percentages.length : null,
        avgFoodCostDishes: percentages.length, dishesCosted: ready.length, dishesTotal: restaurantDecisionRows.length,
        foodsCosted: [...foodCostPairs.values()].filter((pair) => pair.cost != null && pair.currency === 'EUR').length,
        totalSpend: purchaseSummary.amount,
      },
      reliability: { complete: ready.length, partial: restaurantDecisionRows.length - ready.length,
        menuTotal: restaurantDecisionRows.length, reliablePct: restaurantDecisionRows.length ? ready.length / restaurantDecisionRows.length * 100 : 0 },
      foodCostBars: complete.map(({ row, econ }) => ({ label: row.name, pct: econ.foodCostPct!, complete: true, id: row.id }))
        .sort((a, b) => b.pct - a.pct),
      marginBars: complete.map(({ row, econ }) => ({ label: row.name, value: econ.marginNet!, id: row.id }))
        .sort((a, b) => a.value - b.value),
      supplierDonut: byValue(purchaseSummary.bySupplier).map(([id, value]) => ({ label: sById.get(id) ?? 'Senza fornitore', value })),
      foodSpendBars: byValue(purchaseSummary.byFood).map(([id, value]) => ({ id, label: fById.get(id) ?? id, value })),
      spendTrend: [...purchaseSummary.byMonth].sort(([a], [b]) => a.localeCompare(b)).map(([month, value]) => ({ month, value })),
      purchaseQuality: { total: purchaseSummary.total, valued: purchaseSummary.valued, missing: purchaseSummary.missing,
        undated: purchaseIssueLines.filter((line) => purchaseDate(line) == null).length },
      groups: allGroups, periodLabel, target,
      priceChanges: priceChanges.map((change) => ({ ...change, label: fById.get(change.foodId) ?? change.foodId })),
    };
  }, [restaurantDecisionRows, foods, suppliers, foodCostPairs, purchaseSummary, purchaseIssueLines, allGroups, periodLabel, target, priceChanges]);

  // T-346: Report PDF coi grafici + Analisi AI (edge export-foodcost-pdf).
  interface AiStatus { last: { generated_at: string; pdf_url: string | null } | null; quota: { unlimited: boolean; used_this_month: number; cap: number; ai_allowed: boolean } }
  const [pdfBusy, setPdfBusy] = useState<'none' | 'plain' | 'ai'>('none');
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);

  // Costruisce il payload per il PDF dagli aggregati GIÀ calcolati a schermo.
  function buildPdfPayload(): Record<string, unknown> {
    return {
      generated_label: new Date().toLocaleDateString('it-IT'),
      period_label: periodLabel,
      target_pct: target,
      purchase_quality: reportData.purchaseQuality,
      purchase_scope: purchaseFilter,
      scope_label: tenantLabel + ' · tutti i locali dell’azienda',
      kpi: reportData.kpi,
      reliability: reportData.reliability,
      foodCostBars: reportData.foodCostBars.map((b) => ({ label: b.label, value: b.pct, complete: b.complete })),
      marginBars: reportData.marginBars,
      supplierDonut: reportData.supplierDonut,
      foodSpendBars: reportData.foodSpendBars,
      todo: restaurantDecisionRows.filter((row) => row.status !== 'ready').map((row) => ({ name: row.name, kind: 'cost_blocker', detail: row.detail ?? 'Dati da verificare' })),
    };
  }

  async function fetchAiStatus(): Promise<void> {
    const tid = profile?.active_tenant_id;
    if (!tid) return;
    try {
      const { data } = await getSupabase().functions.invoke<AiStatus>('export-foodcost-pdf', {
        body: { mode: 'status', tenant_id: tid },
      });
      if (data) setAiStatus(data);
    } catch { /* status best-effort */ }
  }

  async function generateReportPdf(includeAi: boolean): Promise<void> {
    const tid = profile?.active_tenant_id;
    if (!tid) { setToast('Locale non selezionato'); return; }
    setPdfBusy(includeAi ? 'ai' : 'plain');
    try {
      const { data, error } = await getSupabase().functions.invoke<{ pdf_url?: string }>('export-foodcost-pdf', {
        body: { mode: 'generate', tenant_id: tid, include_ai: includeAi, payload: buildPdfPayload() },
      });
      if (error) {
        // Leggi il corpo dell'errore (403 quota / ruolo) dal context della FunctionsHttpError.
        let code = ''; let msg = '';
        try {
          const b = (await (error as { context?: Response }).context?.json()) as { error?: string; message?: string } | undefined;
          code = b?.error ?? ''; msg = b?.message ?? '';
        } catch { /* noop */ }
        if (code === 'ai_quota_exceeded' || code === 'ai_not_allowed') {
          setToast(msg || 'Analisi AI non disponibile');
          await fetchAiStatus();
          return;
        }
        throw error;
      }
      const url = data?.pdf_url;
      if (!url) throw new Error('Nessun URL restituito');
      await openExternalUrl(url);
      if (includeAi) await fetchAiStatus();
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPdfBusy('none');
    }
  }

  // Carica lo stato AI (ultima generazione + quota) all'apertura del modale export.
  useEffect(() => {
    if (exportOpen) void fetchAiStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportOpen]);

  // Esportazioni Food Cost (Fase 1, client-side dai dati sincronizzati).
  function downloadCsv(filename: string, csv: string): void {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function handleExport(key: string): void {
    const today = new Date().toISOString().slice(0, 10);
    const fById = new Map((foods as FoodRow[]).map((f) => [f.id, f]));
    const sById = new Map((suppliers as SupplierRow[]).map((s) => [s.id, s]));
    const foodName = (id: string) => fById.get(id)?.name ?? id.slice(0, 8);
    const supplierName = (id: string | null) => (id ? sById.get(id)?.name ?? '—' : '—');
    const ctx = { recipesById, foodUnitCost, foodUom };
    // T-441: escludi le varianti dieta (ricette figlie) dall'export food-cost
    // per-piatto, coerente con la lista/report on-screen (righe ~132/143).
    const recipes = (recipesRaw as Recipe[]).filter((r) => r.variant_of_recipe_id == null);
    const expStockIn = filteredPurchaseLines;
    const expFoods: ExpFood[] = (foods as FoodRow[]).map((f) => {
      const pair = foodCostPairs.get(f.id);
      return {
        id: f.id,
        name: f.name ?? f.id.slice(0, 8),
        default_uom: f.default_uom ?? null,
        last_unit_cost: pair?.cost ?? null,
        prev_unit_cost: null,
        last_unit_cost_uom: pair?.uom ?? null,
        last_unit_cost_currency: pair?.currency ?? null,
        last_unit_cost_at: pair?.observedAt ?? f.last_unit_cost_at ?? null,
        supplier_name: supplierName(f.current_supplier_id ?? null),
      };
    });
    let csv = '';
    let name = '';
    switch (key) {
      case 'food-cost': csv = csvFoodCostPerRecipe(recipes.filter((recipe) => recipe.is_food_cost_only || (recipe.selling_price_cents ?? 0) > 0), ctx, defaultVat, new Set(restaurantDecisionRows.filter((row) => row.status !== 'ready').map((row) => row.id))); name = 'food-cost-piatti'; break;
      case 'listino': csv = csvListinoCosti(expFoods); name = 'listino-costi'; break;
      case 'storico': csv = csvStoricoPrezzi(expStockIn, foodName); name = 'storico-prezzi'; break;
      case 'acquisti': csv = csvReportAcquisti(expStockIn, supplierName); name = 'report-acquisti'; break;
      case 'abc': csv = csvAbcIngredienti(expStockIn, foodName); name = 'analisi-abc'; break;
      case 'valorizzazione': {
        if (stockProjection.isLoading) {
          setToast('Giacenza ancora in caricamento: attendi e riprova');
          return;
        }
        if (stockProjection.isError) {
          setToast('Giacenza non disponibile: sincronizza e riprova');
          return;
        }
        if (stockProjection.data.quarantine.length > 0) {
          setToast(
            `Valorizzazione bloccata: ${stockProjection.data.quarantine.length} `
            + 'movimenti hanno unità incompatibili o dati incompleti',
          );
          return;
        }
        const giac = new Map<string, number>();
        const valori = new Map<string, number>();
        let unvaluedLots = 0;
        let unsupportedCurrencies = 0;
        let negativeLots = 0;
        let orphanFoods = 0;
        for (const balance of stockProjection.data.balances) {
          if (Math.abs(balance.onHandQty) <= 1e-9) continue;
          if (balance.onHandQty < 0) {
            negativeLots += 1;
            continue;
          }
          if (!fById.has(balance.foodId)) {
            orphanFoods += 1;
            continue;
          }
          if (balance.onHandValue == null) {
            unvaluedLots += 1;
            continue;
          }
          if (balance.valueCurrency !== 'EUR') {
            unsupportedCurrencies += 1;
            continue;
          }
          giac.set(
            balance.foodId,
            (giac.get(balance.foodId) ?? 0) + balance.onHandQty,
          );
          valori.set(
            balance.foodId,
            (valori.get(balance.foodId) ?? 0) + balance.onHandValue,
          );
        }
        if (negativeLots > 0) {
          setToast(`Valorizzazione bloccata: ${negativeLots} lotti hanno giacenza negativa`);
          return;
        }
        if (orphanFoods > 0) {
          setToast(`Valorizzazione bloccata: ${orphanFoods} lotti non hanno più un alimento in anagrafica`);
          return;
        }
        if (unvaluedLots > 0) {
          setToast(
            `Valorizzazione bloccata: ${unvaluedLots} lotti non hanno un costo trasportato verificabile`,
          );
          return;
        }
        if (unsupportedCurrencies > 0) {
          setToast(
            `Valorizzazione bloccata: ${unsupportedCurrencies} lotti non sono valorizzati in EUR`,
          );
          return;
        }
        csv = csvValorizzazione(giac, expFoods, valori);
        name = 'valorizzazione-magazzino';
        break;
      }
      case 'alert': csv = toCsv(['Articolo', 'Data precedente', 'Data ultima', 'UdM', 'Prezzo precedente EUR', 'Prezzo ultimo EUR', 'Variazione %', 'Effetto prezzo EUR'], priceChanges.filter((row) => Math.abs(row.pct) >= 10).map((row) => [foodName(row.foodId), row.previousDate, row.date, row.uom, row.before, row.current, row.pct, row.impact])); name = 'variazioni-prezzi-confrontabili'; break;
      case 'schede': csv = csvSchedeTecniche(recipes, foodName, ctx); name = 'schede-tecniche-costo'; break;
      default: return;
    }
    downloadCsv(`${name}-${today}.csv`, csv);
    setExportOpen(false);
    setToast('Esportazione scaricata');
  }

  async function saveSellingPrice(r: Recipe): Promise<void> {
    const raw = (draft[r.id] ?? '').trim();
    const euros = Number(raw.replace(',', '.'));
    const cents = raw === '' ? null : Number.isFinite(euros) && euros >= 0 ? Math.round(euros * 100) : undefined;
    if (cents === undefined) throw new Error('Inserisci un prezzo valido maggiore o uguale a zero');
    await enqueueMaster.mutateAsync({
      op: 'update', table: 'recipes', id: r.id,
      patch: { selling_price_cents: cents }, base_version: r.version,
    });
  }

  const greenCount = rows.filter((x) => x.diag.severity === 'green').length;
  const requiredQueries = [recipesQuery, foodsQuery, stockInQuery, stockOutQuery, adjustmentsQuery, documentsQuery];
  if (requiredQueries.some((query) => query.isLoading || query.isError)) {
    const failed = requiredQueries.some((query) => query.isError);
    return <IonPage><IonHeader><IonToolbar><IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons><IonTitle>Food Cost ristorante</IonTitle></IonToolbar></IonHeader>
      <IonContent className="ion-padding">{failed ? <><p>Non è stato possibile leggere tutti i dati. I totali non vengono mostrati incompleti.</p><IonButton onClick={() => void Promise.all(requiredQueries.map((query) => query.refetch()))}>Riprova</IonButton></> : <><IonSpinner /><p>Caricamento dei dati Food Cost…</p></>}</IonContent></IonPage>;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Food Cost ristorante</IonTitle>
          {view !== 'sales' && <IonButtons slot="end">
            <IonButton onClick={() => setExportOpen(true)} aria-label="Esporta report e dati" title="Esportazioni"><IonIcon slot="icon-only" icon={downloadOutline} /></IonButton>
          </IonButtons>}
        </IonToolbar>
        <IonToolbar>
          <IonSegment className="fc-view-nav" value={view} onIonChange={(e) => setView(String(e.detail.value ?? 'report') as typeof view)}>
            <IonSegmentButton value="report"><IonLabel>Sintesi</IonLabel></IonSegmentButton>
            <IonSegmentButton value="purchases"><IonLabel>Acquisti</IonLabel></IonSegmentButton>
            <IonSegmentButton value="list"><IonLabel>Piatti</IonLabel></IonSegmentButton>
            <IonSegmentButton value="checks"><IonLabel>Controlli</IonLabel></IonSegmentButton>
            {(profile?.is_platform_owner || ['consulente','ristoratore'].includes(profile?.role ?? '')) && <IonSegmentButton value="sales"><IonLabel>Vendite</IonLabel></IonSegmentButton>}
          </IonSegment>
        </IonToolbar>
        {view === 'list' && (
          <IonToolbar>
            <IonSearchbar
              value={search}
              debounce={150}
              placeholder="Cerca ricetta…"
              onIonInput={(ev) => setSearch(String(ev.detail.value ?? ''))}
            />
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent className="food-cost-page">
        <div className="fc-shell">
          {view === 'sales' && <SalesImportPanel />}
          {view !== 'sales' && <div className="fc-context"><div><strong>{tenantLabel}</strong><span>Tutti i locali dell’azienda · importi netti IVA in euro</span></div>
            <label>Periodo acquisti<select value={periodKey} onChange={(event) => setPeriodKey(event.target.value)}>
              <option value="month">Mese in corso</option><option value="previous">Mese precedente</option>
              <option value="quarter">Ultimi 3 mesi</option><option value="all">Tutto lo storico datato</option>
            </select></label>
          </div>}
          {(view === 'report' || view === 'purchases') && <FoodCostReport data={reportData} mode={view === 'report' ? 'summary' : 'purchases'}
            filter={purchaseFilter} onFilter={setPurchaseFilter} onChecks={() => setView('checks')}
            onPurchases={() => setView('purchases')} onRecipe={(id) => setFixRecipeId(id)} onDocument={(id) => router.push(`/documents/public/${id}`, 'forward')} />}
          {view === 'checks' && <>
            <div className="fc-section-heading"><h2>Qualità dei dati</h2><p>Verifica il documento prima di rettificare un prezzo. Gli avvisi sono indizi da controllare.</p></div>
            <section className="fc-card"><h2>Piatti da completare o verificare</h2>
              {restaurantDecisionRows.filter((row) => row.status !== 'ready').map((row) => <div className="fc-rank-row" key={row.id}>
                <p><strong>{row.name}</strong> · {row.detail}</p>
                {row.blockerFoodId ? <button className="fc-link" onClick={() => openFoodSourceDocument(row.blockerFoodId!)}>Apri documento di origine</button>
                  : <button className="fc-link" onClick={() => openRecipeEditor(row.id)}>Completa la ricetta</button>}
              </div>)}
              {restaurantDecisionRows.length === 0 ? <p>Nessun piatto configurato: imposta il prezzo o attiva il food cost nelle ricette da analizzare.</p> : restaurantDecisionRows.every((row) => row.status === 'ready') && <p>Nessun piatto registrato richiede integrazioni per il calcolo.</p>}
            </section>
            <FoodCostAnomalyCard anomalies={priceAnomalies} onOpenDocument={openFoodSourceDocument} onManualFix={openFoodCostFix} />
            <section className="fc-card"><h2>Righe escluse o incomplete negli acquisti</h2><p>Righe del periodo e della categoria selezionati prive di importo in euro, più le righe senza data della stessa categoria. Le righe senza data non possono essere assegnate a un periodo.</p>
              {purchaseIssueLines.map((line, index) => <div className="fc-rank-row" key={`${line.document_id}-${index}`}>
                <p>{foods.find((food) => food.id === line.food_id)?.name ?? line.food_id} · {!purchaseDate(line) ? 'Data documento mancante' : 'Importo EUR non disponibile'}</p>
                {line.document_id && <button className="fc-link" onClick={() => router.push(`/documents/public/${line.document_id}`, 'forward')}>Apri documento di questa riga</button>}
              </div>)}
              {purchaseIssueLines.length === 0 && <p>Nessuna riga esclusa per data o importo.</p>}
            </section>
            {priceAnomalies.length === 0 && <p className="fc-note">Nessuna anomalia automatica rilevata sui prezzi disponibili.</p>}
          </>}
          {view === 'list' && <>
            <RestaurantDecisionCockpit rows={restaurantDecisionRows} target={target} onTargetChange={setTarget}
              onFixRecipe={(id) => setFixRecipeId(id)} onOpenDocument={openFoodSourceDocument} />
            <details className="fc-card"><summary>Ricette e preparazioni · modifica dosi e prezzi</summary>
        <>
        <div style={{ padding: '8px 14px', color: '#64748b', fontSize: 13 }}>
          🟢 completa · 🟠 parziale (manca qualche costo) · 🔴 nessun costo. {greenCount}/{rows.length} ricette complete.
          Imposta il prezzo di vendita per food cost % e margine.
        </div>
        <IonList>
          {rows.map(({ recipe, diag }) => {
            const sellEur = recipe.selling_price_cents != null ? recipe.selling_price_cents / 100 : null;
            // T-450 F1: IVA scorporata + porzioni giuste via computeDishEconomics.
            // NIENTE `?? partialCost`: senza porzioni food cost %/margine = N/A
            // (mostriamo l'invito a impostarle) invece di un numero falsato.
            const econ = computeDishEconomics({
              costPerPortion: diag.severity === 'green' && restaurantDecisionRows.find((row) => row.id === recipe.id)?.status === 'ready' ? diag.costPerPortion : null,
              sellingPriceCentsGross: recipe.selling_price_cents ?? null,
              vatRatePct: recipe.vat_rate ?? defaultVat,
            });
            const fcPct = econ.foodCostPct;
            const margin = econ.marginNet;
            const noPortions = diag.costPerPortion == null && diag.severity !== 'red';
            const partial = diag.severity === 'orange';
            const none = diag.severity === 'red';
            return (
              <IonItem key={recipe.id}>
                <span slot="start" aria-hidden style={{ width: 12, height: 12, borderRadius: 6, background: DOT[diag.severity], flex: '0 0 auto' }} />
                <IonLabel className="ion-text-wrap">
                  <h2 style={{ fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}
                    onClick={() => setFixRecipeId(recipe.id)}>
                    {recipe.name} <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>· tocca per sistemare</span>
                  </h2>
                  {none ? (
                    <p style={{ fontSize: 12, color: '#94a3b8' }}>
                      Costo non disponibile{diag.missing[0] ? ` — ${REASON_LABEL[diag.missing[0].reason] ?? diag.missing[0].reason}` : ''}
                    </p>
                  ) : (
                    <p style={{ fontSize: 13, color: '#334155' }}>
                      {partial ? 'Costo parziale: ' : 'Costo: '}<b>{eur(diag.partialCost)}</b>
                      {diag.costPerPortion != null && <> · porzione: <b>{eur(diag.costPerPortion)}</b></>}
                      {fcPct != null && <> · food cost: <b style={{ color: fcPct > 35 ? '#b91c1c' : '#15803d' }}>{fcPct.toFixed(0)}%</b></>}
                      {margin != null && <> · margine: <b>{eur(margin)}</b></>}
                      {noPortions && <span style={{ color: '#d97706' }}> · imposta le porzioni per food cost %/margine</span>}
                      {partial && <span style={{ color: '#d97706' }}> · {diag.total - diag.resolved}/{diag.total} senza costo</span>}
                    </p>
                  )}
                </IonLabel>
                <div slot="end" style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 112 }}>
                  <IonNote style={{ fontSize: 12 }}>€ vendita</IonNote>
                  <IonInput
                    type="number"
                    inputmode="decimal"
                    style={{ maxWidth: 70, textAlign: 'right' }}
                    placeholder={sellEur != null ? sellEur.toFixed(2) : '—'}
                    value={draft[recipe.id] ?? (sellEur != null ? String(sellEur) : '')}
                    onIonInput={(ev) => setDraft((d) => ({ ...d, [recipe.id]: String(ev.detail.value ?? '') }))}

                  />
                  {draft[recipe.id] !== undefined && <IonButton onClick={() => void saveSellingPrice(recipe).then(() => {
                    setDraft((values) => { const next = { ...values }; delete next[recipe.id]; return next; });
                    setToast('Prezzo di vendita salvato');
                  }).catch((error) => setToast(toUserMessage(error)))}>Salva</IonButton>}
                  {/* T-342: apri la ricetta completa per correggere ingredienti/quantità/resa. */}
                  <IonButton fill="clear" size="small" title="Modifica ricetta"
                    onClick={() => openRecipeEditor(recipe.id)}>
                    <IonIcon slot="icon-only" icon={createOutline} />
                  </IonButton>
                </div>
              </IonItem>
            );
          })}
        </IonList>
        </>
            </details>
          </>}
        </div>
      </IonContent>
      <RecipeCostFixModal
        recipeId={fixRecipeId}
        foods={foods as FoodRow[]}
        recipesById={recipesById}
        foodUnitCost={foodUnitCost}
        foodUom={foodUom}
        defaultVatRate={defaultVat}
        onClose={() => setFixRecipeId(null)}
        onEditFull={(id) => { setFixRecipeId(null); openRecipeEditor(id); }}
        onOpenRecipe={(id) => setFixRecipeId(id)}
      />
      {/* T-462: correzione del costo di UN alimento (tap dal Controllo prezzi). */}
      <IonModal isOpen={fixFood != null} onDidDismiss={() => { setFixFood(null); setFixAnomaly(null); }} initialBreakpoint={0.5} breakpoints={[0, 0.5, 0.9]}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{fixAnomaly?.kind === 'unita_miste' ? 'Verifica unità e costo' : 'Correggi costo'}</IonTitle>
            <IonButtons slot="end"><IonButton onClick={() => { setFixFood(null); setFixAnomaly(null); }}>Chiudi</IonButton></IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {fixFood && (
            <>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{fixFood.name ?? 'Alimento'}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                Unità del prodotto: <b>{fixFood.default_uom ?? '—'}</b>
                {fixFood.last_unit_cost != null && (
                  <> · ultimo costo sincronizzato: <b>{fixFood.last_unit_cost.toLocaleString('it-IT', { maximumFractionDigits: 4 })} {fixFood.last_unit_cost_currency ?? 'EUR'}/{fixFood.last_unit_cost_uom ?? '?'}</b></>
                )}
              </div>
              {fixAnomaly?.kind === 'unita_miste' && (
                <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: '1px solid #fde68a', background: '#fffbeb', color: '#78350f', fontSize: 13, lineHeight: 1.45 }}>
                  <b>Perché compare l'allarme</b>
                  <div style={{ marginTop: 4 }}>{fixAnomaly.detail}</div>
                  {fixAnomaly.latestLoadAt && <div style={{ marginTop: 3 }}>Ultimo carico rilevato: {new Date(fixAnomaly.latestLoadAt).toLocaleDateString('it-IT')}.</div>}
                  {normalizedUom(fixAnomaly.latestLoadUom) === normalizedUom(fixFood.default_uom) ? (
                    <IonButton expand="block" color="warning" style={{ marginTop: 10 }} onClick={() => void confirmFoodUom()}>
                      Conferma {fixFood.default_uom} come unità corretta
                    </IonButton>
                  ) : (
                    <div style={{ marginTop: 7, fontWeight: 600 }}>
                      Non confermabile: l'ultimo carico è in {fixAnomaly.latestLoadUom ?? 'unità ignota'}, mentre l'anagrafica è in {fixFood.default_uom ?? '—'}.
                    </div>
                  )}
                  <small style={{ display: 'block', marginTop: 7 }}>
                    La conferma non cancella i carichi precedenti. Un nuovo carico con unità diversa farà riapparire l'allarme.
                  </small>
                </div>
              )}
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#334155' }}>Nuovo costo €/{fixFood.default_uom ?? 'unità'}:</span>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  placeholder="—"
                  style={{ maxWidth: 120, border: '1px solid #cbd5e1', borderRadius: 8, textAlign: 'right' }}
                  value={costDraft}
                  onIonInput={(ev) => setCostDraft(String(ev.detail.value ?? ''))}
                />
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, lineHeight: 1.4 }}>
                Il costo è espresso nell'unità del prodotto ("{fixFood.default_uom ?? 'unità'}"). Vuoto = costo non noto.
                Il costo e la conferma dell'unità sono due operazioni separate.
              </div>
              <IonButton expand="block" style={{ marginTop: 20 }} onClick={() => void saveFoodCostFix()}>
                Salva costo
              </IonButton>
            </>
          )}
        </IonContent>
      </IonModal>
      <IonModal isOpen={exportOpen} onDidDismiss={() => setExportOpen(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Esportazioni Food Cost</IonTitle>
            <IonButtons slot="end"><IonButton onClick={() => setExportOpen(false)}>Chiudi</IonButton></IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {/* T-346: Report PDF coi grafici + Analisi AI (salvata e ri-consultabile). */}
          <div style={{ padding: '12px 14px 0' }}>
            <h3 style={{ fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>📊 Report PDF</h3>
            <IonButton expand="block" onClick={() => void generateReportPdf(false)} disabled={pdfBusy !== 'none'}>
              {pdfBusy === 'plain' ? <IonSpinner name="dots" /> : <><IonIcon slot="start" icon={barChartOutline} /> Scarica report (grafici)</>}
            </IonButton>
            {aiStatus?.quota.ai_allowed && (
              <div style={{ marginTop: 10, background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                  <IonIcon icon={sparklesOutline} style={{ color: '#7c3aed' }} /> Analisi AI
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {aiStatus.quota.unlimited
                    ? 'Illimitata (consulente).'
                    : `${aiStatus.quota.used_this_month}/${aiStatus.quota.cap} usata questo mese.`}
                </div>
                {aiStatus.last && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
                    <span style={{ color: '#334155' }}>Ultima analisi: <b>{new Date(aiStatus.last.generated_at).toLocaleDateString('it-IT')}</b></span>
                    {aiStatus.last.pdf_url && (
                      <IonButton size="small" fill="outline" onClick={() => { const u = aiStatus.last?.pdf_url; if (u) void openExternalUrl(u); }}>
                        <IonIcon slot="start" icon={openOutline} /> Apri
                      </IonButton>
                    )}
                  </div>
                )}
                <IonButton
                  expand="block"
                  style={{ marginTop: 8 }}
                  onClick={() => void generateReportPdf(true)}
                  disabled={pdfBusy !== 'none' || (!aiStatus.quota.unlimited && aiStatus.quota.used_this_month >= aiStatus.quota.cap)}
                >
                  {pdfBusy === 'ai' ? <IonSpinner name="dots" /> : <><IonIcon slot="start" icon={sparklesOutline} /> Genera analisi AI</>}
                </IonButton>
                {!aiStatus.quota.unlimited && aiStatus.quota.used_this_month >= aiStatus.quota.cap && (
                  <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 4 }}>
                    Quota mensile esaurita — riapri l’ultima analisi qui sopra.
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ padding: '14px 14px 0', color: '#64748b', fontSize: 12 }}>Oppure scarica un foglio CSV (apribile in Excel).</div>
          <IonList>
            <IonItem lines="none"><IonLabel><h3 style={{ fontWeight: 700, color: '#15803d' }}>Disponibili ora</h3></IonLabel></IonItem>
            {EXPORTS_ACTIVE.map((ex) => (
              <IonItem key={ex.key} button detail onClick={() => handleExport(ex.key)}>
                <IonIcon icon={documentTextOutline} slot="start" color="success" />
                <IonLabel className="ion-text-wrap">
                  <h3 style={{ fontWeight: 600 }}>{ex.title}</h3>
                  <IonNote>{ex.desc}</IonNote>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
          <p className="fc-note" style={{ padding: 14 }}>Gli acquisti esportati seguono il periodo e la categoria selezionati. Costi dei piatti e listino sono attuali. Queste esportazioni non includono ancora consumo reale, conto economico completo o previsioni di vendita.</p>
        </IonContent>
      </IonModal>
      <IonToast isOpen={toast != null} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}
